import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import HowToSchema from '@/components/HowToSchema';
import { ArrowRight, Building2, CheckCircle } from 'lucide-react';

const STEPS = [
  { name: 'Draw or measure the plan', text: "Start from scratch in the Floor Plan Creator, or upload and measure an existing plan with Plan Measure — either way, you'll end up with an accurate, to-scale floor plan." },
  { name: 'Name every room', text: 'Label each room clearly (Kitchen, Bedroom 1, Bathroom, etc.) — buyers and tenants scan floor plans for room names first.' },
  { name: 'Confirm room extents', text: "Check each room's internal area looks right at a glance — a listing floor plan is often the first thing a buyer measures a property by." },
  { name: 'Show metric and imperial', text: "Display both metres and feet/inches (or switch between them) so the plan reads naturally for every audience, not just one market." },
  { name: 'Calculate GIA', text: "Present the property's total Gross Internal Area (GIA) alongside the individual room areas — a figure agents, buyers and valuers all expect to see." },
  { name: 'Present the marketing layout', text: 'Lay the finished plan out with a north arrow, scale bar and clean room labelling, ready to drop straight into a property listing.' },
];

const HowToCreateEstateAgentFloorPlan = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Estate Agent Floor Plans", path: "/estate-agent-floor-plans" },
    { name: "How to Create an Estate Agent Floor Plan", path: "/guides/how-to-create-an-estate-agent-floor-plan" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="guide-estate-agent-floor-plan"
        defaultTitle="Create an Estate Agent Floor Plan | House Plan Direct"
        defaultDescription="How to create a professional estate agent or real estate floor plan: room names, room extents, metric and imperial, Gross Internal Area (GIA) and presentation, ready for a property listing."
        aiSummary="To create an estate agent or real estate floor plan: draw or measure the property, name every room, confirm each room's extent, show both metric and imperial units, calculate the Gross Internal Area (GIA), then present the layout with a north arrow and scale bar. House Plan Direct's Floor Plan Creator and Present workspace support this whole workflow, or a managed redraw service is available as an alternative."
        keywords="how to create an estate agent floor plan, real estate floor plan, property marketing floor plan, floor plan for property listing, GIA floor plan"
      />
      <HowToSchema
        steps={STEPS}
        name="How to Create an Estate Agent / Real Estate Floor Plan"
        description="Step-by-step guide to creating a professional estate agent / real estate marketing floor plan."
        url="https://houseplandirect.com/guides/how-to-create-an-estate-agent-floor-plan"
      />
      <Header />
      <SeoBreadcrumbs items={breadcrumbs} />

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Building2 className="h-12 w-12 text-orange-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            How to Create an Estate Agent / Real Estate Floor Plan
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Six steps to a clean, professional marketing floor plan ready for a property listing.
          </p>
          <Link to="/floor-plan-creator">
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700" data-testid="guide-agent-link-creator">
              Start Creating Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 mb-10">
            A good estate agent floor plan does two jobs at once: it's an accurate technical drawing, and
            it's a piece of marketing. The steps below cover both — getting the measurements right, and
            presenting them the way buyers and tenants expect to see them.
          </p>

          <ol className="space-y-6 mb-12">
            {STEPS.map((step, i) => (
              <li key={step.name} className="flex gap-4" data-testid={`guide-agent-step-${i + 1}`}>
                <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                <div>
                  <h2 className="font-semibold text-lg text-gray-900 mb-1">{step.name}</h2>
                  <p className="text-gray-600">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="bg-orange-50 border border-orange-200 rounded-xl p-8 mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">What is GIA?</h2>
            <p className="text-gray-700">
              Gross Internal Area (GIA) is the total floor area measured to the internal face of the
              perimeter walls, on every floor. It's the figure most commonly quoted in UK property
              listings, alongside the individual room areas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-orange-600" /> Starting from scratch?</h3>
              <p className="text-sm text-gray-600 mb-3">Draw the property using exact wall measurements, then switch to the Present workspace to lay it out as a marketing plan.</p>
              <Link to="/floor-plan-creator" className="text-orange-600 underline hover:text-orange-700 text-sm font-medium">Open the Floor Plan Creator →</Link>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-orange-600" /> Already have a plan?</h3>
              <p className="text-sm text-gray-600 mb-3">Measure it online first, then present it — no need to redraw a plan that already exists.</p>
              <Link to="/measurement-tool" className="text-orange-600 underline hover:text-orange-700 text-sm font-medium" data-testid="guide-agent-link-measure">Measure an existing plan online →</Link>
            </div>
          </div>

          <p className="text-gray-600 mb-12">
            Prefer someone else to do it for you? Our managed <Link to="/estate-agent-floor-plans" className="text-orange-600 underline hover:text-orange-700">estate agent floor plan service</Link> redraws
            a professional marketing plan from your sketch or existing material, from £40.
          </p>

          <div className="bg-orange-600 text-white rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to create your marketing plan?</h2>
            <p className="text-orange-100 mb-6">Free to create and edit. Unlock the finished project for £2.99.</p>
            <Link to="/floor-plan-creator">
              <Button size="lg" variant="secondary" className="bg-white text-orange-600 hover:bg-gray-100">
                Start Creating Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowToCreateEstateAgentFloorPlan;
